import { GetPageLink } from '@/utility/getLinkPage';
import { useRouter } from 'next/router';
import { useEffect, useState, type FC } from 'react';
import LoadingPage from '../LoadingPage';
import type { AuthCheckerProps } from './types';
import { CompositionRoot } from '../../src/CompositionRoot';
import { DIContainer } from '@/packages/idn-media-framework-tsyringe/DIContainer';
import { IEventBus } from '@/packages/idn-media-framework-abstraction/IEventBus'
import checkForceAuth from '@/components/helpers/checkForceAuth';
import { ILogger } from '@/packages/idn-media-framework-abstraction/ILogger';
import { OAuthFlow } from '@/components/helpers/OAuthFlow';
import { IAuthenticationService } from '@/packages/idn-media-framework-abstraction/IAuthenticationService';
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { API, Auth } from 'aws-amplify';
import { useProfileDetail } from '@/hooks/adapters';
import { useSetupTwoFAStore } from '@/containers/SetupTwoFA/useSetupTwoFAStore';
import { ProfileDetail } from 'api-response';
import { IEventBusSubcription } from '@/packages/idn-media-framework-abstraction/IEventBusSubcription';
import { checkDeviceLimit, checkDeviceLimitFromStorage } from '@/containers/DeviceLimitPopup/checkDeviceLimit';
import DeviceLimitPopup from '@/containers/DeviceLimitPopup';

const AuthChecker: FC<AuthCheckerProps> = ({ children }) => {const isOpen = useSetupTwoFAStore((s) => s.isOpen);
  const beginSetupTwoFA = useSetupTwoFAStore((s) => s.beginSetupTwoFA);
  const redirectionUrl = useSetupTwoFAStore((s) => s.redirectionUrl);
  const router = useRouter();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isDeviceLimitReached, setIsDeviceLimitReached] = useState(false);
  const [deviceLimitCount, setDeviceLimitCount] = useState(0);

  const { isLoading: isLoadingProfileDetail, response: profileDetail } = useProfileDetail({enabled: !!isLoggedIn});

  const handleIdPLogin = (identity_provider: any) => {
    const logger = DIContainer.Resolve<ILogger>('ILogger');
    // Store redirect_uri/authorization_code in local storage to be used to later
    let queryStringParams = new URLSearchParams(window.location.search);
    let redirect_uri = queryStringParams.get('redirect_uri');
    let authorization_code = queryStringParams.get('authorization_code');
    let clientState = queryStringParams.get('state');
  
    if (redirect_uri) {
      logger.debug('Storing redirect_uri to storage:', redirect_uri);
      localStorage.setItem(`client-redirect-uri`, redirect_uri);
    }
    if (authorization_code) {
      logger.debug('Storing authorization_code to storage:', authorization_code);
      localStorage.setItem(`authorization_code`, authorization_code);
    }
    if (clientState) {
      logger.debug('Storing clientState to storage:', clientState);
      localStorage.setItem(`client-state`, clientState);
    }
    
    localStorage.setItem(`social_identity_provider`, identity_provider);
  
    Auth.federatedSignIn({ provider: identity_provider });
  }  

  const handleIdpRelogin = async () => {
    setIsLoadingPage(true);

    let identity_provider = localStorage.getItem('social_identity_provider');
    if (identity_provider == CognitoHostedUIIdentityProvider.Google) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
    } else if (identity_provider == CognitoHostedUIIdentityProvider.Facebook) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Facebook });
    } else if (identity_provider == CognitoHostedUIIdentityProvider.Apple) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Apple });
    } else {
      setIsLoadingPage(false);
    }
  }

  useEffect(()=> {
    let authenticationServiceSubscription: IEventBusSubcription | null = null;

    const init = async () => {
      if(isInitialized) {
        setIsLoadingPage(false);
        // return
      };

      await CompositionRoot.InitializeApplication();

      const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
      await eventBus.CreateTopic('LoginForm');

      await checkForceAuth();

      const loginFormSubscription = eventBus.Subscribe('LoginForm', async e => {

        if(e.eventName === 'SocialLoginButtonClicked'){
          handleIdPLogin(e.data);
        }
      });

      authenticationServiceSubscription = await eventBus.Subscribe('IAuthenticationService', async e => {
        if(e.eventName === 'signIn'){
          const storageResult = await checkDeviceLimitFromStorage();
          if (storageResult?.blocked) {
            setIsDeviceLimitReached(true);
            setDeviceLimitCount(storageResult.deviceCount);
            setIsLoadingPage(false);
            return;
          }

          try {
            setIsLoadingPage(true);

            const deviceLimitResult = await checkDeviceLimit();
            if (deviceLimitResult.blocked) {
              setIsDeviceLimitReached(true);
              setDeviceLimitCount(deviceLimitResult.deviceCount);
              setIsLoadingPage(false);
              return;
            }

            const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

            // show logout button on sso broker when not part of any OAuth flow, otherwise redirect to client web
            if (OAuthFlowSignIn?.isOAuthFlow === false) {
              // set loading state false and show logout button if user login directly from sso broker 
              setIsLoadingPage(false);
              setIsLoggedIn(true);
            } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
              const extraReq = {
                headers: {
                  Authorization: `Bearer ${(await Auth.currentSession())
                    .getAccessToken()
                    .getJwtToken()}`,
                },
              };
              let response: { data?: ProfileDetail } = {};
              try {
                response = await API.get('profile', '/detail', extraReq) as { data?: ProfileDetail };
              } catch (err) {
                console.error('Gagal ambil data profil:', err);
              }
              const profileDetailData = response.data;
              if (router.pathname === '/login' && profileDetailData?.is_2fa_enabled === true && profileDetailData.is_2fa_verified === false) {
                await beginSetupTwoFA();
              }
              // show loading when user redirect to client
              setIsLoadingPage(true);
              if (localStorage.getItem('mandatory_2fa_redirection')) {
                const parsed = JSON.parse(localStorage.getItem('mandatory_2fa_redirection')!);
                const url = parsed?.state?.redirectionUrl;
                useSetupTwoFAStore.persist.clearStorage();
                window.location.replace(url!);
              } else if (OAuthFlowSignIn.redirectUrl) {
                window.location.replace(OAuthFlowSignIn.redirectUrl)
              }
            }
          } catch (error: any) {
            // show logout button on sso broker when authorization_code invalid
            if (error.response?.data == 'Invalid authorization_code') {
              setIsLoggedIn(true);
              setIsLoadingPage(false);
            }
          }
        } else if (e.eventName === 'signOut') {
          setIsLoggedIn(false);
        } else {
          setIsLoadingPage(false);
        }
      })

      const authenticationService = DIContainer.Resolve<IAuthenticationService>('IAuthenticationService');
      await authenticationService.CheckLoggedIn();

      if (router.query.error_description && /already.found.an.entry.for.username/gi.test(
        router.query.error_description.toString(),
      )
      ) {
        handleIdpRelogin();
      }
    }

    init();    
    setIsInitialized(true);
    
    return () => {
      if (authenticationServiceSubscription) {
        const eventBus = DIContainer.Resolve<IEventBus>("IEventBus");
        eventBus.Unsubscribe(authenticationServiceSubscription);
      }
    };
  }, [])

  useEffect(() => {
    const runAsync = async () => {
      if (isLoadingProfileDetail || typeof profileDetail === 'undefined') return;
  
      if (router.pathname === '/login' && profileDetail.is_2fa_enabled === true && profileDetail.is_2fa_verified === false) {
        await beginSetupTwoFA();
      }
  
      if (redirectionUrl) {
        const url = redirectionUrl;
        useSetupTwoFAStore.persist.clearStorage();
        window.location.replace(url);
      } else {
        router.push(GetPageLink('/', router.query));
      }
    };
  
    runAsync();
  }, [isLoggedIn, router.push, router.asPath, router.pathname, isLoadingProfileDetail, profileDetail, redirectionUrl])
  
  return (
    <>
      <DeviceLimitPopup
        isOpen={isDeviceLimitReached}
        deviceCount={deviceLimitCount}
      />
      {(isLoggedIn || isLoadingPage || isLoggedIn === undefined) && <LoadingPage />}
      {(isLoggedIn === false && isLoadingPage === false) && children}
    </>
  )
}

export default AuthChecker;
